"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { categoryLabel, VALID_CATEGORY_SLUGS } from "@/lib/analytics";
import { getNicheColor } from "@/lib/chart-palette";
import type { StartupRecord } from "@/lib/types";

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_LENGTH = 2;

type SearchResult =
  | { type: "category"; slug: string; label: string }
  | { type: "startup"; slug: string; name: string; category: string; categoryLabel: string; icon: string | null };

function getNicheInitials(label: string): string {
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] ?? "") + (words[1][0] ?? "");
  }
  return label.slice(0, 2).toUpperCase() || "?";
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

export function CategorySearch() {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (q: string) => {
    const trimmed = q.trim().toLowerCase();
    if (trimmed.length < SEARCH_MIN_LENGTH) {
      setResults([]);
      return;
    }

    setLoading(true);

    const categoryResults: SearchResult[] = VALID_CATEGORY_SLUGS
      .filter((slug) => {
        const label = categoryLabel(slug).toLowerCase();
        const slugNorm = slug.toLowerCase().replace(/-/g, " ");
        return label.includes(trimmed) || slugNorm.includes(trimmed);
      })
      .map((slug) => ({
        type: "category" as const,
        slug,
        label: categoryLabel(slug)
      }));

    try {
      const res = await fetch(
        `/api/startups?search=${encodeURIComponent(trimmed)}&limit=15`,
        { cache: "no-store" }
      );
      const json = await res.json();
      const startups = (json.data ?? []) as StartupRecord[];

      const startupResults: SearchResult[] = startups.map((s) => ({
        type: "startup" as const,
        slug: s.slug,
        name: s.name,
        category: s.category,
        categoryLabel: categoryLabel(s.category),
        icon: s.icon
      }));

      const seen = new Set<string>();
      const combined: SearchResult[] = [];
      for (const r of categoryResults) {
        if (!seen.has(`cat:${r.slug}`)) {
          seen.add(`cat:${r.slug}`);
          combined.push(r);
        }
      }
      for (const r of startupResults) {
        if (!seen.has(`startup:${r.slug}`)) {
          seen.add(`startup:${r.slug}`);
          combined.push(r);
        }
      }

      setResults(combined);
      setFocusedIndex(combined.length > 0 ? 0 : -1);
    } catch {
      setResults(categoryResults);
      setFocusedIndex(categoryResults.length > 0 ? 0 : -1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  function handleSelect(result: SearchResult) {
    const category = result.type === "category" ? result.slug : result.category;
    setQuery("");
    setResults([]);
    setOpen(false);
    router.push(`/dashboard/${category}`);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open || results.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((i) => (i < results.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((i) => (i > 0 ? i - 1 : results.length - 1));
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[focusedIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="dashboard-navbar-search-wrap" ref={dropdownRef}>
      <div className="dashboard-navbar-search">
        <span className="dashboard-navbar-search-icon">
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search categories or apps..."
          className="dashboard-navbar-search-input"
          aria-label="Search categories or apps"
        />
      </div>

      {open && query.length >= SEARCH_MIN_LENGTH && (
        <div className="dashboard-navbar-search-dropdown">
          {loading && results.length === 0 ? (
            <div className="dashboard-navbar-search-loading">Searching...</div>
          ) : results.length === 0 ? (
            <div className="dashboard-navbar-search-empty">No results</div>
          ) : (
            results.map((result, i) => (
              <button
                key={result.type === "category" ? `cat:${result.slug}` : `startup:${result.slug}`}
                type="button"
                className={`dashboard-navbar-search-item ${i === focusedIndex ? "dashboard-navbar-search-item--focused" : ""}`}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setFocusedIndex(i)}
              >
                {result.type === "category" ? (
                  <>
                    <span
                      className="dashboard-navbar-search-item-icon dashboard-navbar-search-item-icon--niche"
                      style={{ backgroundColor: getNicheColor(result.slug) }}
                    >
                      {getNicheInitials(result.label)}
                    </span>
                    <span className="dashboard-navbar-search-item-content">
                      <span className="dashboard-navbar-search-item-label">{result.label}</span>
                      <span className="dashboard-navbar-search-item-meta">Category</span>
                    </span>
                  </>
                ) : (
                  <>
                    {result.icon ? (
                      <img
                        src={result.icon}
                        alt=""
                        className="dashboard-navbar-search-item-icon dashboard-navbar-search-item-icon--startup"
                        width={28}
                        height={28}
                      />
                    ) : (
                      <span className="dashboard-navbar-search-item-icon dashboard-navbar-search-item-icon--placeholder">
                        {result.name.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <span className="dashboard-navbar-search-item-content">
                      <span className="dashboard-navbar-search-item-label">{result.name}</span>
                      <span className="dashboard-navbar-search-item-meta">{result.categoryLabel}</span>
                    </span>
                  </>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
